import { Grid, Box, FormHelperText } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

import ActionButton from 'components/buttons/index';
import ActionsPopper from 'pages/selections/components/poppers/ActionsPopper';
import {
  GridTable,
  GridTableHead,
  GridTableBody,
  GridTableCell,
  GridTableHeaderCell,
  GridTableTruncatedTypography,
} from 'components/grid-table';

import { SortableTableRow } from './sortable-table-row';
import { VariantLibraryOptionCreateInput } from '../variant-library-option-create-input';

import { ISortableOptionsTable } from './types';
import { VariantLibrayForm } from '../../types';
import { styles } from './styles';

export const SortableOptionsTable = ({ onImportChoices }: ISortableOptionsTable) => {
  const form = useFormContext<VariantLibrayForm>();

  const { fields, append, move, remove } = useFieldArray({
    control: form.control,
    name: 'options',
    keyName: 'id',
  });

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((v) => v.id === active.id);
      const newIndex = fields.findIndex((v) => v.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  const onSubmit = (optionName: string) => {
    append({
      name: optionName,
      orderNo: fields.length,
    });
  };

  const isDraggable = fields.length > 1;

  const optionsErrorMessage = form.formState?.errors?.options?.message;

  return (
    <Grid>
      <Grid sx={styles.importChoices}>
        {!!onImportChoices && (
          <ActionButton
            type="button"
            label="+ Import Choices"
            onClick={onImportChoices}
            variant="secondary"
            className="import-choices-btn"
          />
        )}
        <VariantLibraryOptionCreateInput onSubmit={onSubmit} />
      </Grid>
      {!!fields.length && (
        <Grid sx={{ mt: 5.75 }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={fields.map((v) => v.id)} strategy={verticalListSortingStrategy}>
              <GridTable>
                <GridTableHead>
                  <GridTableHeaderCell width="85%">Name</GridTableHeaderCell>
                  <GridTableHeaderCell width="15%" justify="center">
                    Actions
                  </GridTableHeaderCell>
                </GridTableHead>

                <GridTableBody>
                  {fields?.map(({ id, name }: any, index: number) => (
                    <SortableTableRow key={id} id={id}>
                      {(dragListeners) => (
                        <>
                          <GridTableCell width="85%">
                            <Grid
                              sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
                            >
                              {isDraggable && (
                                <Box
                                  {...dragListeners}
                                  sx={{
                                    cursor: 'grab',
                                    fontSize: 18,
                                    userSelect: 'none',
                                    '&:active': { cursor: 'grabbing' },
                                  }}
                                >
                                  ≡
                                </Box>
                              )}
                              <GridTableTruncatedTypography>{name}</GridTableTruncatedTypography>
                            </Grid>
                          </GridTableCell>
                          <GridTableCell width="15%" justify="center">
                            <ActionsPopper handleDelete={() => onRemove(index)} />
                          </GridTableCell>
                        </>
                      )}
                    </SortableTableRow>
                  ))}
                </GridTableBody>
              </GridTable>
            </SortableContext>
          </DndContext>
        </Grid>
      )}
      {optionsErrorMessage && (
        <FormHelperText sx={{ color: 'red' }}>{optionsErrorMessage}</FormHelperText>
      )}
    </Grid>
  );
};
